import { useCallback } from "react";
import { useApolloClient } from "@apollo/client/react";

import {
  EntityViewAccessSummaryDocument,
  type EntityViewAccessSummaryQuery,
  EntityViewTeamAccessMembersDocument,
  type EntityViewTeamAccessMembersQuery,
  EntityViewsByTypeDocument,
  EntityViewsDocument,
  SetEntityViewPublicAccessLevelDocument,
  SetEntityViewVisibilityDocument,
  ShareEntityViewDocument,
  ShareEntityViewTeamDocument,
  UnshareEntityViewDocument,
} from "#app/lib/data";

import { useObjectSharing } from "./use-object-sharing";

// Workspace-dashboard flavor of useObjectSharing — the same dialog contract
// as useWorkflowSharing, against the entity_view_share table.

const MUTATIONS = {
  share: ShareEntityViewDocument,
  shareTeam: ShareEntityViewTeamDocument,
  unshare: UnshareEntityViewDocument,
  setVisibility: SetEntityViewVisibilityDocument,
  setPublicAccessLevel: SetEntityViewPublicAccessLevelDocument,
};

const selectSummary = (data: EntityViewAccessSummaryQuery | undefined) =>
  data?.entityViewAccessSummary ?? null;

const selectTeamMembers = (data: unknown) =>
  (data as EntityViewTeamAccessMembersQuery | undefined)
    ?.entityViewTeamAccessMembers ?? [];

const mergePage = (
  prev: EntityViewAccessSummaryQuery,
  next: EntityViewAccessSummaryQuery,
  list: "members" | "teams",
): EntityViewAccessSummaryQuery => {
  const p = prev.entityViewAccessSummary;
  const n = next.entityViewAccessSummary;
  return {
    ...next,
    entityViewAccessSummary: {
      ...n,
      members: list === "members" ? [...p.members, ...n.members] : p.members,
      teams: list === "teams" ? [...p.teams, ...n.teams] : p.teams,
    },
  };
};

export function useEntityViewSharing(
  entityViewId: string | null,
  search?: string,
) {
  // The view row carries `visibility` and `isShared` — the page's glyph and
  // the directory's Visibility column read them — so a flip refetches every
  // list that holds the row.
  const client = useApolloClient();
  const onVisibilityChange = useCallback(() => {
    void client.refetchQueries({
      include: [EntityViewsDocument, EntityViewsByTypeDocument],
    });
  }, [client]);

  return useObjectSharing({
    id: entityViewId,
    search,
    idKey: "entityViewId",
    summaryDocument: EntityViewAccessSummaryDocument,
    teamMembersDocument: EntityViewTeamAccessMembersDocument,
    mutations: MUTATIONS,
    selectSummary,
    selectTeamMembers,
    mergePage,
    onVisibilityChange,
  });
}
